// Administración · Enlaces compartidos (T-zhjj, plan 2026-10-30 §2.5):
// la parte sin React de la pantalla. Lo que la tabla rotula, lo que el
// drawer «Crear enlace» ofrece y lo que manda al servidor sale de aquí,
// así los tests lo cubren sin jsdom y la pantalla no repite ternarios.
//
// Lo que el backend permite hoy (routes/share-bundles.ts): un bundle de
// una o más observaciones de UNA misma tienda (SHARE_BUNDLE_CROSS_ASSET
// rechaza mezclar), con destinatario informativo y vencimiento. Sin
// nombre interno ni tipo de contenido: esas columnas llegan con la pieza
// backend y hasta entonces el drawer no las muestra.

import {
  SHARE_BUNDLE_MAX_FINDINGS,
  type FacilityDashboardEntry,
  type ObservationPhaseVocabulary,
  type ShareBundleListItem,
} from "@bitacorapro/api-types";
import { ApiError } from "../../../lib/api/error";
import { isEntryOpen } from "../../../lib/postventa/entryThread";
import {
  normalizeSearch,
  observationHeadline,
  pluralizeLabel,
  storeTitle,
} from "../../../lib/util";

/** Las vigencias que ofrece el drawer (handoff §9: 7 / 30 / 90 días). */
export const VENCE_EN_DIAS = [7, 30, 90] as const;
export type VenceEnDias = (typeof VENCE_EN_DIAS)[number];
export const VENCE_EN_DEFAULT: VenceEnDias = 30;

const DAY_MS = 24 * 60 * 60 * 1000;

/** `expiresAt` del POST: ahora + N días, en ISO. */
export function expiresAtFrom(days: VenceEnDias, now: Date = new Date()): string {
  return new Date(now.getTime() + days * DAY_MS).toISOString();
}

/** «n observaciones» con el sustantivo de la receta. */
export function contenidoLabel(itemCount: number, finding: string, findingPlural: string): string {
  return `${itemCount} ${pluralizeLabel(itemCount, finding, findingPlural).toLowerCase()}`;
}

/** El rótulo de la columna Enlace. El backend no guarda un nombre
 *  interno todavía, así que la fila se reconoce por su destinatario y,
 *  sin él, por lo que contiene («Observaciones · 3»). */
export function enlaceLabel(
  b: Pick<ShareBundleListItem, "recipientLabel" | "itemCount">,
  findingPlural: string
): string {
  const para = b.recipientLabel?.trim();
  if (para) return para;
  return `${findingPlural} · ${b.itemCount}`;
}

/** Lo que registra `share_link_accesses.action` (routes/public.ts), en
 *  palabras. Un valor desconocido se muestra neutro, nunca se oculta. */
export function accesoAccionLabel(action: string | null): string {
  switch (action) {
    case "view":
      return "Abrió el enlace";
    case "update_status":
      return "Cambió el estado";
    case "upload_evidence":
      return "Subió evidencia";
    default:
      return "Acceso";
  }
}

/** Una observación abierta que el drawer puede incluir. */
export type ObservacionOpcion = {
  id: string;
  titulo: string;
  tienda: string;
  assetId: string;
  zona: string | null;
};

/** Lo mínimo que una fila del tablero debe traer para ser una opción:
 *  el rótulo, la tienda y el hilo que decide si sigue abierta. */
export type OpcionEntry = Pick<
  FacilityDashboardEntry,
  | "id"
  | "aiTitle"
  | "humanDescription"
  | "aiDescription"
  | "element"
  | "areaName"
  | "assetId"
  | "assetUnit"
  | "assetAddress"
  | "assetGroupName"
  | "observationStatus"
  | "observationPhase"
  | "reopenCount"
  | "observationClosedAt"
  | "observationClosedReason"
>;

/** Las observaciones abiertas del tablero, en el orden en que llegan. La
 *  apertura la decide el hilo (isEntryOpen), nunca una clave de estado. */
export function opcionesAbiertas(
  dashboard: { entries: readonly OpcionEntry[] } | undefined,
  vocab: ObservationPhaseVocabulary | undefined
): ObservacionOpcion[] {
  if (!dashboard) return [];
  return dashboard.entries
    .filter((e) => isEntryOpen(e, vocab))
    .map((e) => ({
      id: e.id,
      titulo: observationHeadline({
        aiTitle: e.aiTitle,
        humanDescription: e.humanDescription,
        aiDescription: e.aiDescription,
        element: e.element,
        areaName: e.areaName,
      }).text,
      tienda: storeTitle({ unit: e.assetUnit, address: e.assetAddress }),
      assetId: e.assetId,
      zona: e.assetGroupName,
    }));
}

/** Filtro del buscador del selector: título, tienda o zona, sin tildes. */
export function filtrarOpciones(
  opciones: readonly ObservacionOpcion[],
  busqueda: string
): ObservacionOpcion[] {
  const q = normalizeSearch(busqueda.trim());
  if (q.length === 0) return [...opciones];
  return opciones.filter((o) =>
    [o.titulo, o.tienda, o.zona]
      .filter((v): v is string => Boolean(v))
      .some((v) => normalizeSearch(v).includes(q))
  );
}

/** Cuántas tiendas distintas cubre la selección. Más de una no se puede
 *  compartir en un solo enlace (regla del servidor); el drawer lo dice
 *  antes de mandar nada. */
export function tiendasEnSeleccion(
  opciones: readonly ObservacionOpcion[],
  seleccion: readonly string[]
): number {
  const elegidas = new Set(seleccion);
  return new Set(opciones.filter((o) => elegidas.has(o.id)).map((o) => o.assetId)).size;
}

/** Tope de observaciones por enlace: el del body del POST
 *  (`shareBundleCreateBody`, api-types). El drawer lo aplica antes de
 *  mandar nada, igual que la regla de una sola tienda. */
export const MAX_POR_ENLACE = SHARE_BUNDLE_MAX_FINDINGS;

export function excedeTope(seleccion: readonly string[]): boolean {
  return seleccion.length > MAX_POR_ENLACE;
}

/** Lo que el drawer dice cuando el POST falla. Los códigos que
 *  routes/share-bundles.ts y el plugin de auth emiten en esta ruta, en
 *  es-CL; cualquier otro —incluido un mensaje en inglés del servidor—
 *  cae al genérico, nunca se muestra crudo. */
export function errorCrearEnlace(error: unknown, tienda: string): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    switch (error.code) {
      case "SHARE_BUNDLE_CROSS_ASSET":
        return `Todas las observaciones deben pertenecer a la misma ${tienda} para compartirlas en un solo enlace.`;
      case "ADMIN_REQUIRED":
      case "ROLE_READ_ONLY":
        return "Tu rol no puede crear enlaces compartidos.";
      case "FINDING_NOT_FOUND":
        return "Alguna de las observaciones ya no existe o cambió. Vuelve a elegirlas.";
      case "OBSERVATION_LIFECYCLE_NOT_ENABLED":
        return "Esta organización no comparte observaciones por enlace.";
      case "NETWORK":
        return "Sin conexión. Revisa tu red e intenta de nuevo.";
      default:
        return "No pudimos crear el enlace. Intenta de nuevo.";
    }
  }
  return "No pudimos crear el enlace. Intenta de nuevo.";
}
